import React, { useMemo } from "react";
import {
  useJsApiLoader,
  GoogleMap,
  Polygon,
  useGoogleMap,
} from "@react-google-maps/api";
import { parsePolygonToPaths, LatLng } from "../../utils/polygonUtils.ts";

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // US center
const DEFAULT_ZOOM = 4;
const DEFAULT_MAP_CONTAINER_HEIGHT_PX = 400;
const SYNTHETIC_GEOMETRY_RADIUS_METERS = 25;
// Matches backend set_point_neighbors offset (~1 m² postage stamp).
const SYNTHETIC_POLYGON_OFFSET_DEGREES = 0.000005;

const POLYGON_OVERLAY_STYLE = {
  fillColor: "#65FE08",
  fillOpacity: 0.3,
  strokeColor: "#65FE08",
  strokeWeight: 2,
};

const CIRCLE_OVERLAY_STYLE = {
  fillColor: "#65FE08",
  fillOpacity: 0.2,
  strokeColor: "#65FE08",
  strokeOpacity: 1,
  strokeWeight: 3,
};

const defaultMapContainerStyle = (height: string) => ({
  height,
  width: "100%" as const,
});

const getMapOptions = () => ({
  mapTypeId: "satellite" as const,
  mapTypeControl: true,
  mapTypeControlOptions: {
    mapTypeIds: ["satellite", "roadmap"],
  },
  streetViewControl: false,
  fullscreenControl: true,
  zoomControl: false,
  tilt: 0,
  heading: 0,
});

interface FitBoundsProps {
  paths: LatLng[][];
}

function fitMapToPaths(map: google.maps.Map, paths: LatLng[][], padding = 40) {
  const bounds = new google.maps.LatLngBounds();
  paths.forEach((path) => {
    path.forEach((p) => bounds.extend(p));
  });
  map.fitBounds(bounds, padding);
}

const FitBounds: React.FC<FitBoundsProps> = ({ paths }) => {
  const map = useGoogleMap();

  React.useEffect(() => {
    if (map && paths.length > 0) {
      fitMapToPaths(map, paths);
    }
  }, [map, paths]);

  return null;
};

function getPathCenter(path: LatLng[]): LatLng | undefined {
  if (path.length === 0) return undefined;

  const totals = path.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: totals.lat / path.length,
    lng: totals.lng / path.length,
  };
}

function makePostageStampPaths(center: LatLng): LatLng[][] {
  const o = SYNTHETIC_POLYGON_OFFSET_DEGREES;
  const { lat, lng } = center;
  return [
    [
      { lat: lat - o, lng: lng + o },
      { lat: lat + o, lng: lng + o },
      { lat: lat + o, lng: lng - o },
      { lat: lat - o, lng: lng - o },
      { lat: lat - o, lng: lng + o },
    ],
  ];
}

function getPathBounds(paths: LatLng[][]) {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const path of paths) {
    for (const point of path) {
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
    }
  }

  return { minLat, maxLat, minLng, maxLng };
}

function isSyntheticPostageStamp(paths: LatLng[][]): boolean {
  if (paths.length !== 1) return false;

  const { minLat, maxLat, minLng, maxLng } = getPathBounds(paths);
  const threshold = SYNTHETIC_POLYGON_OFFSET_DEGREES * 4;

  return maxLat - minLat <= threshold && maxLng - minLng <= threshold;
}

interface SyntheticMapOverlayProps {
  center: LatLng;
  polygonData: string;
}

// Imperative overlay — React wrapper Circle/Polygon fail to paint after reset/remount.
const SyntheticMapOverlay: React.FC<SyntheticMapOverlayProps> = ({
  center,
  polygonData,
}) => {
  const map = useGoogleMap();

  React.useEffect(() => {
    if (!map) return;

    const { paths } = parsePolygonToPaths(polygonData);
    const pathsToDraw =
      paths.length > 0 ? paths : makePostageStampPaths(center);

    const polygons = pathsToDraw.map(
      (path) =>
        new google.maps.Polygon({
          map,
          paths: path,
          ...POLYGON_OVERLAY_STYLE,
          clickable: false,
        })
    );

    const circle = new google.maps.Circle({
      map,
      center,
      radius: SYNTHETIC_GEOMETRY_RADIUS_METERS,
      ...CIRCLE_OVERLAY_STYLE,
      clickable: false,
    });

    fitMapToPaths(map, pathsToDraw);

    return () => {
      polygons.forEach((polygon) => polygon.setMap(null));
      circle.setMap(null);
    };
  }, [map, center.lat, center.lng, polygonData]);

  return null;
};

interface PolygonMapProps {
  polygonData: string;
  siteName?: string;
  initialCenter?: { lat: number; lng: number };
  mapContainerHeight?: string;
  syntheticGeometry?: boolean;
  overlayCenter?: { lat: number; lng: number };
}

const PolygonMap: React.FC<PolygonMapProps> = ({
  polygonData,
  siteName,
  initialCenter = DEFAULT_CENTER,
  mapContainerHeight = `${DEFAULT_MAP_CONTAINER_HEIGHT_PX}px`,
  syntheticGeometry = false,
  overlayCenter,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const mapContainerStyle = defaultMapContainerStyle(mapContainerHeight);

  const { paths, error } = useMemo(
    () => parsePolygonToPaths(polygonData),
    [polygonData]
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey || "",
  });

  const hasValidPolygon = paths.length > 0;
  const isPlaceholderPolygon =
    hasValidPolygon && isSyntheticPostageStamp(paths);
  const resolvedOverlayCenter =
    overlayCenter ||
    (syntheticGeometry
      ? getPathCenter(paths[0] || []) || initialCenter
      : undefined);
  const showSyntheticOverlay =
    syntheticGeometry &&
    Boolean(resolvedOverlayCenter) &&
    (!hasValidPolygon || isPlaceholderPolygon);
  const mapCenter = showSyntheticOverlay
    ? undefined
    : hasValidPolygon
      ? undefined
      : initialCenter;
  const mapZoom = showSyntheticOverlay
    ? undefined
    : hasValidPolygon
      ? undefined
      : DEFAULT_ZOOM;

  if (!apiKey) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12 }}>Enable Maps JavaScript API</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
        }}
      >
        Failed to load Google Maps.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 8,
            backgroundColor: "#fff3cd",
            color: "#856404",
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={getMapOptions()}
      >
        {showSyntheticOverlay && resolvedOverlayCenter && (
          <SyntheticMapOverlay
            center={resolvedOverlayCenter}
            polygonData={polygonData}
          />
        )}
        {hasValidPolygon && !showSyntheticOverlay && (
          <FitBounds paths={paths} />
        )}
        {hasValidPolygon &&
          !showSyntheticOverlay &&
          paths.map((path, i) => (
            <Polygon key={i} path={path} options={POLYGON_OVERLAY_STYLE} />
          ))}
      </GoogleMap>
      {siteName && (hasValidPolygon || showSyntheticOverlay) && (
        <div style={{ marginTop: 8, fontSize: 14, color: "#666" }}>
          {siteName}
        </div>
      )}
    </div>
  );
};

export default PolygonMap;
